<template>
  <div>
    <v-toolbar flat class="d-print-none">
      <ProductionSelector
        :dates="dates"
        :production="production"
        v-on:update:dates="dates=$event"
        v-on:update:production="production=$event"
      />
      <v-spacer/>
      <v-select
        v-if="dates && dates.length > 1"
        class="day-select"
        label="Production days"
        v-model="daysSelected"
        :items="dayItems"
        multiple
        single-line
        hide-details
        clearable
      />
      <v-spacer/>
      <v-checkbox
        v-model="onlyOrdered"
        label="Only meals with orders"
        hide-details
        class="mt-0 pt-0"
      />
      <v-spacer/>
      <v-btn outlined :disabled="rows.length === 0" @click="downloadCsv">Download CSV</v-btn>
    </v-toolbar>

    <v-progress-linear v-if="loading" :indeterminate="true"/>

    <v-alert v-if="error" type="error" outlined class="ma-4">{{ error }}</v-alert>

    <v-alert v-else-if="!hasDates" type="info" outlined class="ma-4 d-print-none">
      Choose a production to see its chef's choice demand.
    </v-alert>

    <div v-else class="pa-4">
      <div class="report-heading">
        <h2>Chef's Choice Demand</h2>
        <div class="report-dates">{{ datesHeading }}</div>
      </div>

      <table class="demand-table">
        <thead>
        <tr>
          <th class="name-cell">meal name</th>
          <th class="count-cell" v-for="group of groups" :key="group">
            {{ groupLabel(group) }}
          </th>
          <th class="write-cell">Plater/cook</th>
          <th class="check-cell">check</th>
          <th class="write-cell">Manager</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row of rows" :key="row.mealId" data-cy="demand-row">
          <td class="name-cell">{{ row.name }} <span class="meal-id">({{ row.mealId }})</span></td>
          <td class="count-cell" v-for="group of groups" :key="group">
            {{ row.counts[group] || '' }}
          </td>
          <td class="write-cell"></td>
          <td class="check-cell">
            <span class="check-box"></span>
          </td>
          <td class="write-cell"></td>
        </tr>
        <tr v-if="rows.length === 0" data-cy="demand-empty">
          <td :colspan="groups.length + 4" class="empty-row">
            No chef's choice meals for this production.
          </td>
        </tr>
        </tbody>
        <tfoot v-if="rows.length > 0">
        <tr>
          <td class="name-cell"><b>total</b></td>
          <td class="count-cell" v-for="group of groups" :key="group">
            <b>{{ totals[group] || '' }}</b>
          </td>
          <td class="write-cell"></td>
          <td class="check-cell"></td>
          <td class="write-cell"></td>
        </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import moment from 'moment';
import api from '@/api';
import urlState from '@/router/urlState';
import ProductionSelector from '@/components/ProductionSelector.vue';
import {csvExport, dateFormatted} from '@/store/utils';

// chef's choice is one production group of its own on the order ('CC'), but the meals are
// still plated for the local and shipping runs, which is the split this sheet is counting
const GROUP_ORDER = ['local', 'ship', 'other', 'CC'];
const GROUP_LABELS = {ship: 'shipping'};

/** where a production group sits in the columns; anything unexpected goes last */
function groupRank(group) {
  const rank = GROUP_ORDER.indexOf(group);
  return rank === -1 ? GROUP_ORDER.length : rank;
}

/**
 * A chef's choice menu entry's meal id: the menu carries them as '10191CC' (and sometimes
 * as a plain id), while an order carries the number on its own.
 */
export function chefsChoiceMealId(id) {
  const number = parseInt(`${id}`.replace(/CC$/i, ''), 10);
  return Number.isFinite(number) ? number : null;
}

/**
 * The chef's choice catalogue out of a menu: those meals are offered for the whole
 * production rather than a day, so they sit in the menu entry dated 'any'. Every stream
 * carries the same entry, so the first one that has it is the list, deduplicated by meal.
 */
export function chefsChoiceCatalogue(menu) {
  const byMealId = new Map();
  Object.values(menu || {}).forEach((stream) => {
    ((stream && stream.menu) || [])
      .filter(entry => entry && entry.date === 'any')
      .forEach((entry) => {
        (entry.products || []).forEach((product) => {
          const mealId = chefsChoiceMealId(product.id);
          if (mealId === null || byMealId.has(mealId)) {
            return;
          }
          byMealId.set(mealId, {mealId, name: `${product.name || ''}`.trim()});
        });
      });
  });
  return [...byMealId.values()];
}

export default {
  name: 'ChefsChoiceDemand',
  components: {ProductionSelector},
  mixins: [urlState],
  data() {
    return {
      dates: [],
      production: undefined,
      daysSelected: [],
      onlyOrdered: false,
      catalogue: [],
      orders: [],
      loading: false,
      error: null,
      fetchedFor: null,
    };
  },
  mounted() {
    this.syncToUrl({
      param: 'dates', urlParam: 'dates', initFromRoute: true,
      parseCallback: v => (Array.isArray(v) ? v : [v]).filter(Boolean),
    });
    this.syncToUrl({param: 'production', urlParam: 'production', initFromRoute: true});
    this.syncToUrl({
      param: 'daysSelected', urlParam: 'days', initFromRoute: true,
      parseCallback: v => (Array.isArray(v) ? v : [v]).filter(Boolean),
    });
    this.syncToUrl({
      param: 'onlyOrdered', urlParam: 'ordered', initFromRoute: true,
      parseCallback: v => v === true || v === 'true',
    });
    return this.fetchData();
  },
  watch: {
    dates: 'fetchData',
  },
  computed: {
    ...mapGetters(['getMeal']),
    hasDates() {
      return !!(this.dates && this.dates.length > 0);
    },
    sortedDates() {
      return [...(this.dates || [])].filter(Boolean).sort();
    },
    dateFrom() {
      return this.sortedDates[0];
    },
    dateTo() {
      return this.sortedDates[this.sortedDates.length - 1];
    },
    dayItems() {
      return this.sortedDates.map(date => ({
        value: date,
        text: dateFormatted(date, {formatString: 'ddd, MMM D'}),
      }));
    },
    /** the production days this sheet counts: every day of the production unless some are picked */
    daysCounted() {
      const picked = (this.daysSelected || []).filter(date => this.sortedDates.includes(date));
      return picked.length > 0 ? picked.sort() : this.sortedDates;
    },
    datesHeading() {
      const days = this.daysCounted;
      if (days.length === 0) {
        return '';
      }
      const format = 'ddd, MMM D';
      if (days.length === 1) {
        return moment(days[0]).format(format);
      }
      return `${moment(days[0]).format(format)} - ${moment(days[days.length - 1]).format(format)}`;
    },
    countedOrders() {
      const days = this.daysCounted;
      return this.orders.filter(order => days.includes(order.date));
    },
    /** quantity per meal per production group, over the days being counted */
    demand() {
      const byMeal = new Map();
      this.countedOrders.forEach((order) => {
        const mealId = chefsChoiceMealId(order.meal_id);
        if (mealId === null) {
          return;
        }
        const counts = byMeal.get(mealId) || {};
        const group = order.productionGroup || 'other';
        counts[group] = (counts[group] || 0) + (order.quantity || 0);
        byMeal.set(mealId, counts);
      });
      return byMeal;
    },
    /** the columns: the production groups that actually have chef's choice demand */
    groups() {
      const found = new Set();
      this.demand.forEach(counts => Object.keys(counts).forEach(group => found.add(group)));
      return [...found].sort((a, b) => groupRank(a) - groupRank(b) || a.localeCompare(b));
    },
    /**
     * One row per chef's choice meal: the whole catalogue, so a meal nobody ordered is still
     * on the sheet, plus anything ordered that is no longer on the menu.
     */
    rows() {
      const byMealId = new Map();
      this.catalogue.forEach(({mealId, name}) => byMealId.set(mealId, {mealId, name}));
      this.demand.forEach((counts, mealId) => {
        if (!byMealId.has(mealId)) {
          byMealId.set(mealId, {mealId, name: this.mealName(mealId)});
        }
      });
      const rows = [...byMealId.values()].map((row) => {
        const counts = this.demand.get(row.mealId) || {};
        const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
        return {...row, name: row.name || this.mealName(row.mealId), counts, total};
      });
      return rows
        .filter(row => !this.onlyOrdered || row.total > 0)
        .sort((a, b) => a.name.trim().localeCompare(b.name.trim()) || a.mealId - b.mealId);
    },
    totals() {
      const totals = {};
      this.rows.forEach(row => this.groups.forEach((group) => {
        totals[group] = (totals[group] || 0) + (row.counts[group] || 0);
      }));
      return totals;
    },
  },
  methods: {
    ...mapActions(['fetchMeal']),
    groupLabel(group) {
      return GROUP_LABELS[group] || group;
    },
    mealName(mealId) {
      const meal = this.getMeal(mealId);
      if (meal && meal.name) {
        return meal.name;
      }
      const named = this.orders.find(o => chefsChoiceMealId(o.meal_id) === mealId && o.mealName);
      return named ? named.mealName : `meal ${mealId}`;
    },
    async fetchData() {
      if (!this.hasDates) {
        return;
      }
      const key = this.sortedDates.join(',');
      if (this.fetchedFor === key) {
        return;
      }
      this.fetchedFor = key;
      this.loading = true;
      this.error = null;
      try {
        const [menu, orders] = await Promise.all([
          api.get(`v2/menu/${this.dateFrom}`).then(({data}) => data),
          api
            .get('v2/order/search', {params: {from: this.dateFrom, to: this.dateTo, productions: ['all']}})
            .then(({data}) => data),
        ]);
        this.catalogue = chefsChoiceCatalogue(menu);
        this.orders = orders.filter(order => order.order_type === 'chefs_choice');
        await this.fetchMissingMealNames();
      } catch (e) {
        console.error('chef\'s choice demand failed', e);
        this.fetchedFor = null;
        this.error = (e.response && e.response.data && e.response.data.error) || e.message || `${e}`;
      } finally {
        this.loading = false;
      }
    },
    /** a meal with orders but no menu entry has no name yet; the store knows it by id */
    async fetchMissingMealNames() {
      const known = new Set(this.catalogue.map(({mealId}) => mealId));
      const missing = [...new Set(this.orders
        .map(order => chefsChoiceMealId(order.meal_id))
        .filter(mealId => mealId !== null && !known.has(mealId) && !this.getMeal(mealId)))];
      await Promise.all(missing.map(mealId => this.fetchMeal(mealId)
        .catch(e => console.warn('could not load meal', mealId, e.message))));
    },
    downloadCsv() {
      // csvExport takes the column names from the keys of the first record
      const rows = this.rows.map((row) => {
        const record = {'meal id': row.mealId, name: row.name};
        this.groups.forEach((group) => {
          record[this.groupLabel(group)] = row.counts[group] || 0;
        });
        record.total = row.total;
        return record;
      });
      csvExport(rows, `chefs-choice-demand-${this.daysCounted.join('-')}.csv`);
    },
  },
};
</script>

<style scoped>
.day-select {
  max-width: 320px;
}

.report-heading {
  margin-bottom: 12px;
}

.report-heading h2 {
  font-size: 18px;
  font-weight: 600;
}

.report-dates {
  font-size: 13px;
}

.demand-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.demand-table th,
.demand-table td {
  border: 1px solid #000;
  padding: 3px 6px;
  vertical-align: middle;
}

.demand-table thead th {
  background: #e8f0de;
  font-weight: 600;
  text-align: left;
  /* the header repeats when the sheet runs over a page */
  break-inside: avoid;
}

.demand-table tbody tr {
  break-inside: avoid;
}

.name-cell {
  width: 51%;
}

/* the id is there to look a meal up by, so it stays out of the way of the name */
.meal-id {
  color: #555;
  white-space: nowrap;
}

/* just the group name, so the count columns stay narrow; the dates are in the heading */
.count-cell {
  width: 6%;
  text-align: right;
  white-space: nowrap;
}

.write-cell {
  width: 14%;
}

.check-cell {
  width: 6%;
  text-align: center;
}

.check-box {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 1px solid #000;
}

.empty-row {
  text-align: center;
  padding: 12px;
}

@media print {
  .demand-table thead {
    display: table-header-group;
  }

  /* the last row is a grand total, not a per page subtotal, so it prints once at the end
     rather than repeating at the foot of every page */
  .demand-table tfoot {
    display: table-row-group;
  }
}
</style>
